import React, { useState } from 'react';
import PageHero from '../components/site/PageHero';
import { company } from '../mock';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', interest: '', message: '' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast({ title: 'Missing info', description: 'Please add your name and phone number.' });
      return;
    }
    try {
      setBusy(true);
      await axios.post(`${API}/leads`, { ...form, source: 'contact_page' });
      toast({ title: 'Thank you!', description: 'Our team will reach out within 24 hours.' });
      setForm({ name: '', email: '', phone: '', interest: '', message: '' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: err?.response?.data?.detail || 'Please try again.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHero eyebrow="Get in Touch" title="Let&rsquo;s find your view." subtitle="Talk to a real human. No bots. No pressure." image="https://images.unsplash.com/photo-1661333587578-8ef88d297b1f?w=1920&q=80" />
      <section className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px w-12 bg-[#8a6b3a]" />
                <span className="text-[#8a6b3a] text-xs uppercase tracking-[0.3em]">Reach us</span>
              </div>
              <h2 className="font-serif text-4xl text-[#0f1a14] leading-tight">Come visit, or send us a note.</h2>
            </div>
            {[
              { Icon: MapPin, title: 'Office', text: company.address },
              { Icon: Phone, title: 'Phone', text: company.phone, href: `tel:${company.phone}` },
              { Icon: Mail, title: 'Email', text: company.email, href: `mailto:${company.email}` },
              { Icon: Clock, title: 'Hours', text: 'Mon – Sat · 9:00 AM to 7:00 PM IST' },
            ].map(({ Icon, title, text, href }) => (
              <div key={title} className="flex gap-4">
                <div className="h-11 w-11 shrink-0 border border-[#8a6b3a] grid place-items-center">
                  <Icon className="h-5 w-5 text-[#8a6b3a]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#8a6b3a]">{title}</div>
                  {href ? <a href={href} className="text-[#0f1a14] hover:text-[#8a6b3a] transition-colors">{text}</a> : <div className="text-[#0f1a14]">{text}</div>}
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={submit} className="lg:col-span-3 bg-[#f6f1e7] p-8 md:p-12 space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-widest text-[#8a6b3a]">Name</label>
                <Input className="mt-2 bg-white border-[#0f1a14]/15 rounded-none h-12" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#8a6b3a]">Phone</label>
                <Input className="mt-2 bg-white border-[#0f1a14]/15 rounded-none h-12" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#8a6b3a]">Email</label>
                <Input type="email" className="mt-2 bg-white border-[#0f1a14]/15 rounded-none h-12" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#8a6b3a]">I&apos;m interested in</label>
                <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
                  <SelectTrigger className="mt-2 bg-white border-[#0f1a14]/15 rounded-none h-12">
                    <SelectValue placeholder="Choose one" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="plot">Freehold Plot</SelectItem>
                    <SelectItem value="villa">Villa</SelectItem>
                    <SelectItem value="farmhouse">Farmhouse</SelectItem>
                    <SelectItem value="visit">Site Visit</SelectItem>
                    <SelectItem value="other">Just exploring</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8a6b3a]">Message</label>
              <Textarea className="mt-2 bg-white border-[#0f1a14]/15 rounded-none min-h-[140px]" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </div>
            <Button type="submit" disabled={busy} className="bg-[#0f1a14] text-white hover:bg-[#1c2e26] rounded-none px-8 py-6 tracking-wider">{busy ? 'Sending...' : 'Send Enquiry'}</Button>
            <p className="text-xs text-[#0f1a14]/50">We respond within 24 hours on business days.</p>
          </form>

        </div>

        <section className="mx-auto max-w-7xl px-6 lg:px-10">
          <br/><br/>
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3480.2782830353294!2d79.5454731!3d29.2741573!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39a09b25b3566659%3A0xae65fde32b4e6eb7!2sEwen%20Realtors%20Private%20Limited!5e0!3m2!1sen!2sin!4v1785863755246!5m2!1sen!2sin"
            className="w-full h-[60vh] border-0"
            loading="lazy"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            title="Ewen Realtors Location"
          />
        </section>

      </section>
    </div>
  );
}
